// src/pages/admin/VehicleForm.jsx
import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { getFeatureCatalog } from "../../api/featureCatalog";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";
import { RxCross2 } from "react-icons/rx";

function FeatureList({
  nestIndex,
  control,
  suggestions = [],
  errors,
  disabled = false,
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `interiorFeatures.${nestIndex}.features`,
  });

  useEffect(() => {
    if (!disabled && fields.length === 0) append("");
  }, [disabled, fields.length, append]);

  const featureValues =
    useWatch({
      control,
      name: `interiorFeatures.${nestIndex}.features`,
    }) || [];

  return (
    <div className="pl-4">
      {fields.map((feat, featIndex) => {
        const errorMsg =
          errors?.interiorFeatures?.[nestIndex]?.features?.[featIndex]?.message;
        return (
          <div key={feat.id} className="flex flex-col mb-2">
            <div className="flex items-center">
              <Controller
                control={control}
                name={`interiorFeatures.${nestIndex}.features.${featIndex}`}
                render={({ field }) => (
                  <div className="relative flex-1">
                    <Combobox
                      value={field.value}
                      onChange={field.onChange}
                      disabled={disabled}
                    >
                      <div className="relative">
                        <ComboboxInput
                          disabled={disabled}
                          placeholder={
                            disabled
                              ? "Select a category first"
                              : "Select or type feature"
                          }
                          displayValue={(val) => val || ""}
                          onChange={(e) =>
                            !disabled && field.onChange(e.target.value)
                          }
                          onFocus={(e) =>
                            !disabled && e.currentTarget.nextSibling?.click()
                          }
                          onClick={(e) =>
                            !disabled && e.currentTarget.nextSibling?.click()
                          }
                          className={
                            "w-full border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary " +
                            (disabled
                              ? "bg-gray-100 cursor-not-allowed opacity-50"
                              : "")
                          }
                        />
                        {!disabled && (
                          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2">
                            <ChevronDownIcon
                              className="w-5 h-5 text-primary"
                              aria-hidden="true"
                            />
                          </ComboboxButton>
                        )}
                      </div>
                      {!disabled && (
                        <ComboboxOptions className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border rounded shadow-lg max-h-48 overflow-auto">
                          {suggestions
                            .filter(
                              (f) =>
                                f === field.value || !featureValues.includes(f)
                            )
                            .map((f) => (
                              <ComboboxOption
                                key={f}
                                value={f}
                                className={({ active }) =>
                                  `px-2 py-1 cursor-pointer ${
                                    active
                                      ? "bg-primary text-white"
                                      : "text-primary"
                                  }`
                                }
                              >
                                {f}
                              </ComboboxOption>
                            ))}
                        </ComboboxOptions>
                      )}
                    </Combobox>
                  </div>
                )}
              />
              <button
                type="button"
                onClick={() => remove(featIndex)}
                className="ml-2 p-1 rounded hover:bg-red-100 focus:outline-none"
                aria-label="Remove feature"
              >
                <XMarkIcon className="h-5 w-5 text-red-500" />
              </button>
            </div>
            {errorMsg && (
              <p className="mt-1 text-red-500 text-xs">{errorMsg}</p>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => append("")}
        className="mt-1 text-sm text-white hover:underline"
      >
        + Add Feature
      </button>
    </div>
  );
}

function determineWarrantyPackage({ mileage, regDate, vehicleType = "car" }) {
  if (!mileage || !regDate || !vehicleType) return null;

  const ageYears = Math.floor(
    (new Date().getTime() - new Date(regDate).getTime()) /
      (1000 * 60 * 60 * 24 * 365.25)
  );

  const m = parseFloat(mileage);

  // Apply rules
  if (vehicleType === "car") {
    if (m <= 70000 && ageYears <= 7) return "Exclusive";
    if (m <= 120000 && ageYears < 12) return "Premier";
    if (m <= 150000 && ageYears < 15) return "Dynamic";
    return "Ignition";
  }

  if (vehicleType === "light") {
    if (m <= 120000 && ageYears < 12) return "Premier";
    if (m <= 150000 && ageYears < 15) return "Dynamic";
    return "Ignition";
  }

  // Fallback
  return "Ignition";
}

export default function VehicleForm({
  initialData = {},
  onSubmit,
  isEdit = false,
  onCancel,
}) {
  // ─── FIXED: use the flat initialData keys, not initialData.base / .extended ─────
  const defaultValues = {
    ...initialData, // spread every top-level field you logged
    vehicleType: initialData.vehicleType || "car",
    regDate: initialData.regDate
      ? initialData.regDate.split("T")[0]
      : undefined, // ✅ convert ISO to 'YYYY-MM-DD'
    mot: initialData.mot || [],
    interiorFeatures: initialData.interiorFeatures || [],
    videoLink: initialData.videoLink || "", // ✅ add this
  };

  console.log(initialData);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ defaultValues });

  // MOT history field-array
  const {
    fields: motFields,
    append: appendMot,
    remove: removeMot,
  } = useFieldArray({ control, name: "mot" });

  // Interior Features field-array
  const {
    fields: catFields,
    append: appendCat,
    remove: removeCat,
  } = useFieldArray({ control, name: "interiorFeatures" });

  const onSubmitInternal = (data) => {
    data.warrantyPackage = livePackage; // 💾 set it before saving
    onSubmit(data);
  };

  // feature catalog
  const { data: catalog = [] } = useQuery({
    queryKey: ["featureCatalog"],
    queryFn: getFeatureCatalog,
  });
  const categories = catalog.map((c) => c.category);
  const selectedCats = watch("interiorFeatures")
    ?.map((f) => f.category)
    .filter(Boolean);

  // ─── field definitions ───────────────────────────────────────
  const basicFields = [
    { name: "registration", label: "Registration", type: "text" },
    { name: "plate", label: "Plate", type: "text" },
    { name: "group", label: "Group", type: "text" },
    { name: "modelDescription", label: "Model Description", type: "text" },
    { name: "regDate", label: "Registration Date", type: "date" },
    { name: "extras", label: "Extras", type: "text" },
    { name: "estP11D", label: "P11D (£)", type: "text" },
    { name: "co2", label: "CO₂ (g/km)", type: "text" },
    { name: "color", label: "Colour", type: "text" },
    { name: "mileage", label: "Mileage", type: "text" },
    { name: "price", label: "Price (£)", type: "text" },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "draft", label: "Draft" },
        { value: "available", label: "Available" },
        { value: "reserved", label: "Reserved" },
        { value: "sold", label: "Sold" },
        { value: "take-off", label: "Take Off" },
      ],
    },
    {
      name: "vehicleType",
      label: "Vehicle Type",
      type: "select",
      options: [
        { value: "car", label: "Car" },
        { value: "light", label: "Light Commercial" },
        { value: "heavy", label: "Heavy Motor Vehicle" },
      ],
    },
    { name: "videoLink", label: "Video Link (optional)", type: "text" },
  ];

  const extFields = [
    { name: "make", label: "Make", type: "text" },
    { name: "model", label: "Model", type: "text" },
    { name: "extColor", label: "Exterior Colour", type: "text" },
    { name: "year", label: "Year", type: "text" },
    { name: "fuel", label: "Fuel", type: "text" },
    { name: "bodyStyle", label: "Body Style", type: "text" },
    { name: "transmissionType", label: "Transmission", type: "text" },
    { name: "cylinderCapacity", label: "Engine CC", type: "text" },
    { name: "engineSizeLitres", label: "Engine L", type: "text" },
    { name: "bhp", label: "BHP", type: "text" },
    { name: "cylinders", label: "Cylinders", type: "text" },
    { name: "valves", label: "Valves", type: "text" },
    { name: "maxNetPower", label: "Max Power", type: "text" },
    // { name: "co2Emissions", label: "CO₂ Emissions", type: "text" },
    { name: "seats", label: "Seats", type: "text" },
    { name: "gears", label: "Gears", type: "text" },
    { name: "doors", label: "Doors", type: "text" },
    // { name: "vin", label: "VIN", type: "text" },
    // { name: "vrm", label: "VRM", type: "text" },
  ];

  const liveRegDate = watch("regDate");
  const liveMileage = watch("mileage");
  const liveVehicleType = watch("vehicleType");

  const livePackage = determineWarrantyPackage({
    regDate: liveRegDate,
    mileage: liveMileage,
    vehicleType: liveVehicleType,
  });

  const { orderId } = initialData;
  const status = watch("status");

  // console.log("liveRegDate:", liveRegDate);
  // console.log("liveMileage:", liveMileage);
  // console.log("liveVehicleType:", liveVehicleType);
  // console.log("livePackage", livePackage);

  return (
    <div className="bg-primary p-4 rounded shadow-md">
      {/* header */}
      <div className="flex justify-between mb-6">
        <h3 className="text-xl">{isEdit ? "Edit Vehicle" : "Add Vehicle"}</h3>
        <button onClick={onCancel}>
          <RxCross2 className="h-6 w-6" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit(onSubmitInternal)}
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        {/* Basic */}
        {basicFields.map(({ name, label, type, options }) => {
          const isRegField = isEdit && name === "registration";
          return (
            <div key={name} className="flex flex-col">
              <label className="mb-1">{label}</label>
              {type === "select" ? (
                <select
                  {...register(name)}
                  disabled={isRegField}
                  className={
                    "border rounded text-primary py-1 " +
                    (isRegField
                      ? "bg-gray-100 cursor-not-allowed opacity-50"
                      : "")
                  }
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  disabled={isRegField}
                  {...register(name, {
                    ...(type === "number" && {
                      setValueAs: (v) => (v === "" ? undefined : parseFloat(v)),
                    }),
                    ...(type === "date" && {
                      valueAsDate: true,
                      setValueAs: (v) => (v === "" ? undefined : v),
                    }),
                  })}
                  className={
                    "border rounded px-2 py-1 " +
                    (isRegField
                      ? "bg-gray-100 cursor-not-allowed opacity-50"
                      : "")
                  }
                />
              )}
              {errors[name] && (
                <p className="text-red-600 text-sm">{errors[name].message}</p>
              )}
            </div>
          );
        })}

        {/* Warranty */}
        {/* {isEdit && ( */}
        <div className="col-span-2 md:col-span-4 border-t pt-10">
          <h4 className="font-semibold mb-4">Warranty Details</h4>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex flex-col">
              <label className="mb-1">Package Name</label>
              <input
                type="text"
                value={livePackage || "N/A"}
                readOnly
                className="border rounded px-2 py-1 bg-gray-100 cursor-not-allowed text-primary"
              />
            </div>
            {status === "sold" && orderId && (
              <div>
                <p>Order ID:</p>
                <div className="mb-4 p-2 bg-white text-primary text-sm rounded">
                  <p>{orderId}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* )} */}

        {/* Extended */}
        {isEdit && (
          <div className="col-span-2 md:col-span-4 border-t pt-10">
            <h4 className="font-semibold mb-4">Extended Details</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {extFields.map(({ name, label, type }) => (
                <div key={name} className="flex flex-col">
                  <label className="mb-1">{label}</label>
                  <input
                    type={type}
                    {...register(name, {
                      ...(type === "number" && {
                        setValueAs: (v) =>
                          v === "" ? undefined : parseFloat(v),
                      }),
                    })}
                    className="border rounded px-2 py-1"
                  />
                  {errors[name] && (
                    <p className="text-red-600 text-sm">
                      {errors[name].message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MOT History */}
        {isEdit && motFields.length > 0 && (
          <div className="col-span-2 md:col-span-4 border-t pt-6">
            <h4 className="font-semibold mb-4">MOT History</h4>
            {motFields.map((m, idx) => (
              <div key={m.id} className="border rounded p-4 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {/* testDate */}
                  <div className="flex flex-col">
                    <label>Test Date</label>
                    <input
                      type="date"
                      {...register(`mot.${idx}.testDate`, {
                        valueAsDate: true,
                        setValueAs: (v) => (v === "" ? undefined : v),
                      })}
                      className="border rounded px-2 py-1"
                    />
                  </div>
                  {/* odometer */}
                  <div className="flex flex-col">
                    <label>Odometer</label>
                    <input
                      type="number"
                      {...register(`mot.${idx}.odometer`, {
                        setValueAs: (v) =>
                          v === "" ? undefined : parseFloat(v),
                      })}
                      className="border rounded px-2 py-1"
                    />
                  </div>
                  {/* testResult */}
                  <div className="flex flex-col">
                    <label>Result</label>
                    <select
                      {...register(`mot.${idx}.testResult`)}
                      className="border rounded px-2 py-1"
                    >
                      <option value={true}>Pass</option>
                      <option value={false}>Fail</option>
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeMot(idx)}
                  className="mt-2 px-3 py-1 bg-red-100 text-red-600 rounded"
                >
                  Remove Entry
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => appendMot({})}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              + Add MOT Entry
            </button>
          </div>
        )}

        {/* Interior Features */}
        <div className="col-span-2 md:col-span-4 border-t pt-10">
          <h4 className="font-semibold mb-4">Interior Features</h4>
          {catFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {catFields.map((cat, catIndex) => {
                const chosen = watch(`interiorFeatures.${catIndex}.category`);
                const featSuggestions =
                  catalog.find((c) => c.category === chosen)?.features || [];
                const categoryError =
                  errors?.interiorFeatures?.[catIndex]?.category?.message;

                return (
                  <div
                    key={cat.id}
                    className="border rounded p-3 flex flex-col"
                  >
                    <div className="flex items-center mb-2">
                      <Controller
                        control={control}
                        name={`interiorFeatures.${catIndex}.category`}
                        render={({ field }) => (
                          <div className="relative flex-1">
                            <Combobox
                              value={field.value}
                              onChange={field.onChange}
                            >
                              <div className="relative">
                                <ComboboxInput
                                  placeholder="Select or type category"
                                  displayValue={(val) => val || ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  onFocus={(e) =>
                                    e.currentTarget.nextSibling?.click()
                                  }
                                  onClick={(e) =>
                                    e.currentTarget.nextSibling?.click()
                                  }
                                  className="w-full border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2">
                                  <ChevronDownIcon
                                    className="w-5 h-5 text-primary"
                                    aria-hidden="true"
                                  />
                                </ComboboxButton>
                              </div>
                              <ComboboxOptions className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border rounded shadow-lg max-h-60 overflow-auto">
                                {categories
                                  .filter(
                                    (c) =>
                                      c === field.value ||
                                      !selectedCats.includes(c)
                                  )
                                  .map((c) => (
                                    <ComboboxOption
                                      key={c}
                                      value={c}
                                      className={({ active }) =>
                                        `px-2 py-1 cursor-pointer ${
                                          active
                                            ? "bg-primary text-white"
                                            : "text-primary"
                                        }`
                                      }
                                    >
                                      {c}
                                    </ComboboxOption>
                                  ))}
                              </ComboboxOptions>
                            </Combobox>
                          </div>
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => removeCat(catIndex)}
                        className="ml-2 p-1 rounded hover:bg-red-100 focus:outline-none"
                        aria-label="Remove category"
                      >
                        <XMarkIcon className="h-5 w-5 text-red-500" />
                      </button>
                    </div>
                    {categoryError && (
                      <p className="mb-2 text-red-500 text-xs">
                        {categoryError}
                      </p>
                    )}
                    <FeatureList
                      nestIndex={catIndex}
                      control={control}
                      suggestions={featSuggestions}
                      errors={errors}
                      disabled={!chosen}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={() => appendCat({ category: "", features: [""] })}
            className="self-start mt-2 px-4 py-2 bg-surface rounded hover:bg-accent"
          >
            + Add Category
          </button>
        </div>

        {/* Actions */}
        <div className="col-span-2 md:col-span-4 flex justify-end gap-4 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-accent text-white rounded"
          >
            {isEdit ? "Update" : "Add"} Vehicle
          </button>
        </div>
      </form>
    </div>
  );
}
